import { IAdresse } from 'components/AddressTypeahead/types';
import {
  ChangeRequestCommand,
  Grossist,
} from 'services/KjederegisteretAdminApi/ChangeRequestApi/types';
import { EndringsMelding } from 'services/QAApi/types';

// Helper class to transfer values to ChangeRequestCommand
// Safeguards that the command is updated correctly
export class ChangeRequestCommandHelper {
  static transferNedlagtDato = (command: ChangeRequestCommand, nedlagtDato: Date) => {
    command.nedlagtDato = nedlagtDato;
  };

  static transferKundeNr = (command: ChangeRequestCommand, kundenr: string) => {
    // add input to array if it does not already exist
    if (command.leverandorKundenummer.indexOf(kundenr) === -1) {
      command.leverandorKundenummer.push(kundenr);
    }
  };

  static transferEnhetnavn = (command: ChangeRequestCommand, enhetnavn: string) => {
    command.enhetnavn = enhetnavn;
  };

  static transferGln = (
    command: ChangeRequestCommand,
    gln: string,
    changeRequest: EndringsMelding,
  ) => {
    // we only accept gln values from certain sources
    if (
      changeRequest.sentBySystem === 'GlnApi' ||
      changeRequest.sentBy === 'GlnSnitch' ||
      changeRequest.sentBy === 'GlnApi' ||
      changeRequest.sentBy === 'Salgsdata'
    ) {
      command.gln = gln;
    }
  };

  static transferOrganisasjonsnr = (
    command: ChangeRequestCommand,
    organisasjonsnr: string,
    juridiskNavn: string | undefined,
  ) => {
    command.organisasjonsnr = organisasjonsnr;
    command.juridisknavn = juridiskNavn;
  };

  static transferTelefon = (command: ChangeRequestCommand, telefon: string) => {
    command.telefon = telefon;
  };

  static transferFakturaEpost = (command: ChangeRequestCommand, fakturaEpost: string) => {
    command.fakturaEpost = fakturaEpost;
  };

  static transferEpost = (command: ChangeRequestCommand, epost: string) => {
    command.epost = epost;
  };

  static transferHjemmeside = (command: ChangeRequestCommand, hjemmeside: string) => {
    command.hjemmeside = hjemmeside;
  };

  static transferDagligLeder = (command: ChangeRequestCommand, dagligLeder: string) => {
    command.dagligLeder = dagligLeder;
  };

  static transferOmsetning = (command: ChangeRequestCommand, omsetning: number | null) => {
    command.omsetning = omsetning;
  };

  static transferKontonummer = (command: ChangeRequestCommand, kontonummer: string) => {
    command.kontonummer = kontonummer;
  };

  static transferSalgsareal = (command: ChangeRequestCommand, salgsareal: number) => {
    command.salgsareal = salgsareal;
  };

  static transferOppstartsDato = (command: ChangeRequestCommand, oppstartsDato: Date) => {
    command.oppstartsDato = oppstartsDato;
  };

  static transferGrossistKundenummer = (command: ChangeRequestCommand, grossist: Grossist) => {
    if (grossist) {
      if (!command.hovedGrossist) {
        command.hovedGrossist = {
          kundenummerHosGrossist: grossist.kundenummerHosGrossist,
          grossistGln: grossist.grossistGln,
          grossistEnhetNavn: grossist.grossistEnhetNavn,
        };
      } else {
        if (!command.andreGrossister) {
          command.andreGrossister = [];
        }
        // check if the grossist already exists in the list
        if (!ChangeRequestCommandHelper.grossistKundenummerExists(command, grossist)) {
          // if not, add it to the list
          command.andreGrossister.push({
            kundenummerHosGrossist: grossist.kundenummerHosGrossist,
            grossistGln: grossist.grossistGln,
            grossistEnhetNavn: grossist.grossistEnhetNavn,
          });
        }
      }
    }
  };

  static grossistKundenummerExists = (command: ChangeRequestCommand, grossist: Grossist) => {
    if (
      command.hovedGrossist?.kundenummerHosGrossist === grossist.kundenummerHosGrossist &&
      command.hovedGrossist?.grossistGln === grossist.grossistGln
    ) {
      return true;
    }
    return (
      command.andreGrossister?.some(
        (g) =>
          g.kundenummerHosGrossist === grossist.kundenummerHosGrossist &&
          g.grossistGln === grossist.grossistGln,
      ) ?? false
    );
  };

  static transferBesoksadresse = (command: ChangeRequestCommand, besoksadresse: IAdresse) => {
    command.besoksadresse = { ...besoksadresse, adressetype: 'Besøksadresse' };
  };

  // Medlemskap transfer functions
  static transferMarkedskjedeInnmelding = (
    command: ChangeRequestCommand,
    kjedeNr: string,
    innmeldingsdato: Date,
    medlemsnummer?: string,
  ) => {
    command.markedskjedeNr = Number(kjedeNr);
    command.markedskjedeInnmeldingsdato = innmeldingsdato;
    if (medlemsnummer) {
      command.markedskjedeMedlemsnummer = medlemsnummer;
    }
  };

  static transferMarkedskjedeUtmelding = (
    command: ChangeRequestCommand,
    kjedeNr: string,
    utmeldingsdato: Date,
  ) => {
    command.markedskjedeNr = Number(kjedeNr);
    command.markedskjedeUtmeldingsdato = utmeldingsdato;
  };

  static transferRegionalkjedeInnmelding = (
    command: ChangeRequestCommand,
    kjedeNr: string,
    innmeldingsdato: Date,
    medlemsnummer?: string,
  ) => {
    command.regionalkjedeNr = Number(kjedeNr);
    command.regionalkjedeInnmeldingsdato = innmeldingsdato;
    if (medlemsnummer) {
      command.regionalkjedeMedlemsnummer = medlemsnummer;
    }
  };

  static transferRegionalkjedeUtmelding = (
    command: ChangeRequestCommand,
    kjedeNr: string,
    utmeldingsdato: Date,
  ) => {
    command.regionalkjedeNr = Number(kjedeNr);
    command.regionalkjedeUtmeldingsdato = utmeldingsdato;
  };

  static transferEnhetsgrupperingInnmelding = (
    command: ChangeRequestCommand,
    enhetsgrupperingNr: number,
    innmeldingsdato: Date,
    medlemsnummer?: string,
  ) => {
    command.enhetsgrupperingsNr = enhetsgrupperingNr;
    command.enhetsgrupperingsInnmeldingsdato = innmeldingsdato;
    if (medlemsnummer) {
      command.enhetsgrupperingsMedlemsnummer = medlemsnummer;
    }
  };

  static transferEnhetsgrupperingUtmelding = (
    command: ChangeRequestCommand,
    enhetsgrupperingNr: number,
    utmeldingsdato: Date,
  ) => {
    command.enhetsgrupperingsNr = enhetsgrupperingNr;
    command.enhetsgrupperingsUtmeldingsdato = utmeldingsdato;
  };
}
